import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/lib/apiClient';

interface CadenceConfig {
  id: string | null;
  dayOfWeek: number;
  hourUtc: number;
  recipientRoles: string[];
  enabled: boolean;
}

const ALL_ROLES = ['TENANT_ADMIN', 'SALES_MANAGER', 'SALES_REPRESENTATIVE'] as const;
const DAYS = [0, 1, 2, 3, 4, 5, 6] as const;

export function RevenueCadenceAdminPage() {
  const { t } = useTranslation();
  const [config, setConfig] = useState<CadenceConfig>({
    id: null,
    dayOfWeek: 1,
    hourUtc: 8,
    recipientRoles: ['TENANT_ADMIN', 'SALES_MANAGER'],
    enabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get<CadenceConfig>('/admin/revenue-cadence');
      setConfig(data);
    } catch {
      // config not set yet, use defaults
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save() {
    setSaving(true);
    try {
      await apiClient.put('/admin/revenue-cadence', config);
      showToast(t('revenueCadence.saved'));
      load();
    } catch {
      showToast(t('revenueCadence.saved') + ' — error');
    } finally {
      setSaving(false);
    }
  }

  async function generateNow() {
    setGenerating(true);
    try {
      await apiClient.post('/admin/revenue-cadence/generate');
      showToast(t('revenueCadence.generated'));
    } catch {
      showToast(t('revenueCadence.generated') + ' — error');
    } finally {
      setGenerating(false);
    }
  }

  function toggleRole(role: string) {
    setConfig((prev) => {
      const roles = prev.recipientRoles.includes(role)
        ? prev.recipientRoles.filter((r) => r !== role)
        : [...prev.recipientRoles, role];
      return { ...prev, recipientRoles: roles };
    });
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <Breadcrumbs crumbs={[{ label: t('revenueCadence.admin_title') }]} />

      {toast && (
        <div className="fixed bottom-4 right-4 bg-foreground text-background px-4 py-2 rounded shadow z-50 text-sm">
          {toast}
        </div>
      )}

      <div className="flex items-center justify-between mt-2 mb-6">
        <div>
          <h1 className="text-2xl font-semibold">{t('revenueCadence.admin_title')}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t('revenueCadence.admin_subtitle')}</p>
        </div>
        <Button variant="outline" onClick={generateNow} disabled={generating}>
          {generating ? t('revenueCadence.generating') : t('revenueCadence.generate_now')}
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
      ) : (
        <div className="border rounded-xl p-6 space-y-6 bg-card">
          <h2 className="font-medium">{t('revenueCadence.config_title')}</h2>

          <div>
            <label className="text-sm font-medium">{t('revenueCadence.field_enabled')}</label>
            <div className="mt-2 flex items-center gap-3">
              <button
                type="button"
                role="switch"
                aria-checked={config.enabled}
                onClick={() => setConfig((prev) => ({ ...prev, enabled: !prev.enabled }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.enabled ? 'bg-primary' : 'bg-muted'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-card shadow transition-transform ${config.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <span className="text-sm text-muted-foreground">{config.enabled ? 'On' : 'Off'}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium">{t('revenueCadence.field_day')}</label>
              <select
                className="mt-1 w-full border rounded-md bg-background px-3 py-2 text-sm"
                value={config.dayOfWeek}
                onChange={(e) => setConfig((prev) => ({ ...prev, dayOfWeek: parseInt(e.target.value) }))}
              >
                {DAYS.map((d) => (
                  <option key={d} value={d}>{t(`revenueCadence.day_${d}`)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">{t('revenueCadence.field_hour')}</label>
              <Input
                className="mt-1"
                type="number"
                min={0}
                max={23}
                value={config.hourUtc}
                onChange={(e) => setConfig((prev) => ({ ...prev, hourUtc: parseInt(e.target.value) || 0 }))}
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">{t('revenueCadence.field_roles')}</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {ALL_ROLES.map((role) => (
                <button
                  key={role}
                  type="button"
                  onClick={() => toggleRole(role)}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                    config.recipientRoles.includes(role)
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {role.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving}>
              {saving ? t('revenueCadence.saving') : t('revenueCadence.save')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
